"use client";

import { startTransition, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react";

import styles from "./styles/Header.module.scss";

import NavigationPrimary from "./NavigationPrimary";
import NavigationSecondary from "./NavigationSecondary";
import dynamic from "next/dynamic";
import { useStores } from "../../stores/useStores";
import { useShouldDisplayHero } from "../../hooks/useShouldDisplayHero";
import { useTranslateClassName } from "../../hooks/useTranslateClassName";
import { extractNumber } from "../../utils/StringUtilities";
import { cssValue } from "../../utils/SchoolBlocksUtilities";

const HeroContainer = dynamic(() => import("./HeroContainer"));

interface IHeaderProps {
  displayViewHomepageButton?: boolean;
  isWelcomePage?: boolean;
}

const Header = observer((props: IHeaderProps) => {
  const { interfaceStore, organizationStore } = useStores();
  const displayHero = useShouldDisplayHero();
  const translateClassName = useTranslateClassName();

  const { displayViewHomepageButton = false, isWelcomePage = false } = props;

  const alwaysShowHeader =
    organizationStore.currentOrganization.json_data.settings?.appearance
      ?.alwaysShowHeader;

  const [visible, setVisible] = useState(
    (!displayHero || alwaysShowHeader) && !isWelcomePage,
  );
  const secondaryNavRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // this is running in a useEffect because scrollPosition is always 0 on SSR so otherwise a reload while scrolled
    // down doesn't hydrate with the correct classNames and it doesn't update
    const primaryNavHeight = extractNumber(cssValue("--primary-nav-height"));
    startTransition(() => {
      if (
        alwaysShowHeader ||
        interfaceStore.scrollPosition > primaryNavHeight ||
        !displayHero
      ) {
        setVisible(true);
      } else if (visible) {
        setVisible(false);
      }
    });
  }, [interfaceStore.scrollPosition]);

  const secondaryNavHeight = useMemo(() => {
    if (secondaryNavRef?.current) {
      return secondaryNavRef.current.offsetHeight;
    }
    return 0;
  }, [secondaryNavRef?.current]);

  return (
    <>
      <ul className={`${styles.skip} ${translateClassName} skiptocontent`}>
        <li key={"navigation"}>
          <a
            href={
              interfaceStore.breakpoint === "break-point-lg"
                ? "#navigation"
                : "#MobileNavigationMenu"
            }
            tabIndex={2}
          >
            Skip to Navigation
          </a>
        </li>
      </ul>
      <NavigationPrimary
        isWelcomePage={isWelcomePage}
        heroIsEnabled={displayHero}
        visible={visible}
      />
      {displayHero && (
        <HeroContainer
          displayViewHomepageButton={displayViewHomepageButton}
          isWelcomePage={isWelcomePage}
          secondaryNavHeight={secondaryNavHeight}
        />
      )}
      <span ref={secondaryNavRef}>
        <NavigationSecondary
          isWelcomePage={isWelcomePage}
          visible={visible}
          heroIsEnabled={displayHero}
        />
      </span>
    </>
  );
});

export default Header;
